/**
 * EditRelationshipModal component
 *
 * Modal for editing a saved relationship (person's birth data).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CityAutocomplete } from './CityAutocomplete';
import type { Relationship } from '../types';
import type { GeocodeSuggestion } from '../api/charts';
import type { UpdateRelationshipData } from '../api/relationships';

interface EditRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: number, data: UpdateRelationshipData) => Promise<void>;
  relationship: Relationship | null;
}

const RELATIONSHIP_TYPES = [
  'partner',
  'spouse',
  'friend',
  'family',
  'child',
  'parent',
  'sibling',
  'colleague',
  'other'
];

export function EditRelationshipModal({
  isOpen,
  onClose,
  onSave,
  relationship
}: EditRelationshipModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form with relationship data
  useEffect(() => {
    if (relationship && isOpen) {
      setName(relationship.name || '');
      setRelationshipType(relationship.relationship_type || '');
      setBirthDate(relationship.birth_date || '');
      setBirthTime(relationship.birth_time || '');
      setBirthPlace(relationship.birth_place || '');
      setNotes(relationship.notes || '');
      setError(null);
    }
  }, [relationship, isOpen]);

  const handleCitySelect = (suggestion: GeocodeSuggestion) => {
    setBirthPlace(suggestion.display_name);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!relationship) return;

    if (!name) {
      setError(t('profile.editModal.nameRequired'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(relationship.id, {
        name,
        relationship_type: relationshipType,
        birth_date: birthDate,
        birth_time: birthTime,
        birth_place: birthPlace,
        notes
      });
      onClose();
    } catch (err) {
      console.error('Failed to save relationship:', err);
      setError(t('errors.actionFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !relationship) return null;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={e => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2>{t('profile.editModal.editPerson')}</h2>
          <button className="edit-modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="edit-modal-body">
          {error && <div className="edit-modal-error">{error}</div>}

          <div className="edit-form-group">
            <label>{t('profile.editModal.name')}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              disabled={isSaving}
              placeholder={t('profile.editModal.namePlaceholder')}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.editModal.relationship')}</label>
            <select
              value={relationshipType}
              onChange={e => setRelationshipType(e.target.value)}
              disabled={isSaving}
            >
              <option value="">{t('profile.editModal.selectRelationship')}</option>
              {RELATIONSHIP_TYPES.map(type => (
                <option key={type} value={type}>
                  {t(`profile.relationshipTypes.${type}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="edit-form-group">
            <label>{t('profile.birthDateLabel')}</label>
            <input
              type="date"
              value={birthDate}
              onChange={e => setBirthDate(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.birthTimeLabel')}</label>
            <input
              type="time"
              value={birthTime}
              onChange={e => setBirthTime(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.birthPlaceLabel')}</label>
            <CityAutocomplete
              value={birthPlace}
              onChange={setBirthPlace}
              onSelect={handleCitySelect}
              disabled={isSaving}
            />
          </div>

          <div className="edit-form-group">
            <label>{t('profile.editModal.notes')}</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={isSaving}
              rows={3}
              placeholder={t('profile.editModal.notesPlaceholder')}
            />
          </div>

          <div className="edit-modal-actions">
            <button
              type="button"
              className="edit-modal-btn cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="edit-modal-btn save"
              disabled={isSaving}
            >
              {isSaving ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditRelationshipModal;
